import React, { useState, useEffect } from "react";
import { useNode, useEditor } from "@craftjs/core";
import { FormControl, FormLabel, TextField, Button as MuiButton, Select, MenuItem, Checkbox, FormControlLabel } from "@mui/material";
import { useTemplateString } from './useTemplateString';
import { useTextPersistence } from "qwanyx";

// Simple markdown to HTML converter
const markdownToHtml = (markdown) => {
  if (!markdown) return '';
  
  return markdown
    // Headers
    .replace(/^### (.*$)/gim, '<h3>$1</h3>')
    .replace(/^## (.*$)/gim, '<h2>$1</h2>')
    .replace(/^# (.*$)/gim, '<h1>$1</h1>')
    // Bold
    .replace(/\*\*(.*?)\*\*/gim, '<strong>$1</strong>')
    .replace(/__(.*?)__/gim, '<strong>$1</strong>')
    // Italic
    .replace(/\*(.*?)\*/gim, '<em>$1</em>')
    .replace(/_(.*?)_/gim, '<em>$1</em>')
    // Code blocks
    .replace(/```([\s\S]*?)```/gim, '<pre><code>$1</code></pre>')
    // Inline code
    .replace(/`(.*?)`/gim, '<code>$1</code>')
    // Links
    .replace(/\[([^\]]+)\]\(([^)]+)\)/gim, '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>')
    // Line breaks
    .replace(/\n/gim, '<br>');
};

export const MarkdownDisplay = ({
  name,
  markdownContent = "",
  fontSize = "14px",
  fontFamily = 'Arial',
  color = '#000000',
  backgroundColor,
  borderRadius = "0px",
  padding = "10px",
  hasBorder = true,
  border = "1px solid #e0e0e0",
  hasCustomHeight = false,
  height = "200px",
  enableScroll = false,
  cssClass,
  qwantumId,
  onSave,
  style,
  hidden,
  ...props
}) => {
  const { 
    connectors: { connect, drag }, 
    hasSelectedNode,
    isSelected,
    isHovered,
    actions: { setProp },
    parent
  } = useNode((state) => ({
    hasSelectedNode: state.events.selected,
    isSelected: state.events.selected,
    isHovered: state.events.hovered,
    parent: state.data.parent
  }));

  const { enabled } = useEditor((state) => ({
    enabled: state.options.enabled
  }));

  // Hook for template strings
  const { processTemplateString, hasContext } = useTemplateString();

  // Persistence hook - only use if qwantumId is provided
  const { text: persistedContent, handleBlur: persistenceBlur } = useTextPersistence(qwantumId, onSave);
  const [prevValue, setPrevValue] = useState(markdownContent || persistedContent || "");

  useEffect(() => {
    !hasSelectedNode && setEditable(false);
  }, [hasSelectedNode]);

  const [editable, setEditable] = useState(false);

  if (hidden) return null; 

  // Use persisted content if qwantumId exists, otherwise use prop content
  const sourceContent = qwantumId ? (persistedContent || "") : markdownContent;
  
  // Process template strings
  const processedContent = processTemplateString(sourceContent);
  
  // Convert markdown to HTML
  const htmlContent = markdownToHtml(processedContent);

  const handleBlur = (e) => {
    setEditable(false);
    
    // Handle persistence if qwantumId is provided
    if (qwantumId) {
      const newValue = e.target.value;
      
      // Only persist if value has changed
      if (newValue !== prevValue) {
        // Create a synthetic event for persistenceBlur
        const syntheticEvent = {
          target: { value: newValue }
        };
        persistenceBlur(syntheticEvent);
        
        if (onSave) {
          onSave(newValue);
        }
        setPrevValue(newValue);
      }
    }
  };

  const getSelectionStyle = () => {
    if (isSelected && !editable) {
      return {
        outline: '1px dashed #2196f3',
        outlineOffset: '2px'
      };
    }
    if (isHovered && !editable) {
      return {
        outline: '1px dashed #ff9800',
        outlineOffset: '1px'
      };
    }
    return {};
  };

  return (
    <div 
      ref={ref => connect(drag(ref))}
      name={name}
      className={cssClass}
      onClick={() => enabled && setEditable(true)}
      style={{
        transition: 'all 0.2s ease',
        backgroundColor: backgroundColor,
        borderRadius: borderRadius,
        padding: padding,
        fontFamily: fontFamily,
        fontSize: fontSize,
        color: color,
        minHeight: hasCustomHeight ? 'auto' : '50px',
        height: hasCustomHeight ? height : 'auto',
        overflow: hasCustomHeight && enableScroll ? 'auto' : 'visible',
        border: hasBorder ? border : 'none',
        ...style,
        ...getSelectionStyle()
      }}
      {...props}
    >
      {editable && enabled ? (
        <TextField
          multiline
          fullWidth
          rows={10}
          variant="outlined"
          value={sourceContent}
          onChange={(e) => setProp(props => props.markdownContent = e.target.value)}
          onBlur={handleBlur}
          placeholder="Enter your markdown content here..."
          autoFocus
          sx={{
            '& .MuiOutlinedInput-root': {
              fontSize: fontSize,
              fontFamily: fontFamily,
            }
          }}
        />
      ) : (
        <div
          style={{
            fontSize: fontSize,
            fontFamily: fontFamily,
            color: color,
            lineHeight: 1.6,
            wordBreak: 'break-word'
          }}
          dangerouslySetInnerHTML={{ __html: htmlContent }}
        />
      )}
    </div>
  );
};

const MarkdownDisplaySettings = () => {
  const { 
    actions: { setProp }, 
    name, markdownContent, fontSize, fontFamily, color,
    backgroundColor, borderRadius, padding, hasBorder, border, 
    hasCustomHeight, height, enableScroll, cssClass, qwantumId,
    parent
  } = useNode((node) => ({
    name: node.data.props.name,
    markdownContent: node.data.props.markdownContent,
    fontSize: node.data.props.fontSize,
    fontFamily: node.data.props.fontFamily,
    color: node.data.props.color,
    backgroundColor: node.data.props.backgroundColor,
    borderRadius: node.data.props.borderRadius,
    padding: node.data.props.padding,
    hasBorder: node.data.props.hasBorder,
    border: node.data.props.border,
    hasCustomHeight: node.data.props.hasCustomHeight,
    height: node.data.props.height,
    enableScroll: node.data.props.enableScroll,
    cssClass: node.data.props.cssClass,
    qwantumId: node.data.props.qwantumId,
    parent: node.data.parent
  }));

  // Hook for template context detection
  const { hasContext, itemContext } = useTemplateString();

  const { query } = useEditor();
  const parentNode = parent ? query.node(parent).get() : null;

  // Copy to clipboard function
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(markdownContent || '');
      alert('Markdown content copied to clipboard!');
    } catch (err) {
      console.error('Failed to copy: ', err);
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = markdownContent || '';
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      alert('Markdown content copied to clipboard!');
    }
  };

  // Paste from clipboard function
  const handlePaste = async () => {
    try {
      const text = await navigator.clipboard.readText();
      setProp(props => props.markdownContent = text);
      alert('Content pasted from clipboard!');
    } catch (err) {
      console.error('Failed to paste: ', err);
      alert('Please paste manually in the text area or grant clipboard permissions.');
    }
  };

  return (
    <div>
      {parentNode && (
        <FormControl fullWidth margin="normal">
          <FormLabel>Parent: {parentNode.data.displayName || parentNode.data.name || 'Container'}</FormLabel>
        </FormControl>
      )}
      
      <FormControl fullWidth margin="normal">
        <FormLabel>Component Name</FormLabel>
        <TextField
          value={name || ''}
          onChange={(e) => setProp(props => props.name = e.target.value)}
          size="small"
          placeholder="Enter component name"
        />
      </FormControl>

      <FormControl fullWidth margin="normal">
        <FormLabel>Quantum ID</FormLabel>
        <TextField
          value={qwantumId || ''}
          onChange={(e) => setProp(props => props.qwantumId = e.target.value)}
          size="small"
          placeholder="Enter quantum ID for persistence"
        />
      </FormControl>

      <FormControl fullWidth margin="normal">
        <FormLabel>Markdown Content</FormLabel>
        <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
          <MuiButton
            variant="outlined"
            size="small"
            onClick={handlePaste}
            sx={{ flex: 1 }}
          >
            📋 Paste from Clipboard
          </MuiButton>
          <MuiButton
            variant="outlined"
            size="small"
            onClick={handleCopy}
            sx={{ flex: 1 }}
            disabled={!markdownContent}
          >
            📄 Copy to Clipboard
          </MuiButton>
        </div>
        <TextField
          value={markdownContent || ''}
          onChange={(e) => setProp(props => props.markdownContent = e.target.value)}
          size="small"
          multiline
          rows={8}
          placeholder={hasContext ? "Use ${item.name}, ${item.title}, ${item.description}... or enter markdown" : "Enter markdown content"}
          helperText="Supports: # Headers, **bold**, *italic*, `code`, [links](url), ```code blocks```"
        />
        {hasContext && (
          <div style={{ 
            fontSize: '12px', 
            color: '#2196f3', 
            marginTop: '4px',
            padding: '8px',
            backgroundColor: 'rgba(33, 150, 243, 0.1)',
            borderRadius: '4px',
            border: '1px solid rgba(33, 150, 243, 0.3)'
          }}>
            💡 <strong>Template strings available:</strong><br/>
            <code>${"{item.name}"}</code>, <code>${"{item.title}"}</code>, <code>${"{item.description}"}</code>, <code>${"{item.status}"}</code>, <code>${"{index}"}</code>
            {itemContext && (
              <div style={{ marginTop: '4px' }}>
                <strong>Current item:</strong> {JSON.stringify(itemContext.item, null, 2).substring(0, 100)}...
              </div>
            )}
          </div>
        )}
      </FormControl>

      <FormControl fullWidth margin="normal">
        <FormLabel>Font Size</FormLabel>
        <TextField
          value={fontSize || '14px'}
          onChange={(e) => setProp(props => props.fontSize = e.target.value)}
          size="small"
          placeholder="e.g. 14px, 1.2em, 16pt, 1rem"
          helperText="Use any CSS unit: px, em, rem, pt, %, etc."
        />
      </FormControl>

      <FormControl fullWidth margin="normal">
        <FormLabel>Font Family</FormLabel>
        <Select
          value={fontFamily || 'Arial'}
          onChange={(e) => setProp(props => props.fontFamily = e.target.value)}
          size="small"
        >
          <MenuItem value="Arial">Arial</MenuItem>
          <MenuItem value="Helvetica">Helvetica</MenuItem>
          <MenuItem value="Montserrat">Montserrat</MenuItem>
          <MenuItem value="Times New Roman">Times New Roman</MenuItem>
          <MenuItem value="Georgia">Georgia</MenuItem>
          <MenuItem value="Verdana">Verdana</MenuItem>
          <MenuItem value="Courier New">Courier New</MenuItem>
          <MenuItem value="Comic Sans MS">Comic Sans MS</MenuItem>
        </Select>
      </FormControl>

      <FormControl fullWidth margin="normal" component="fieldset">
        <FormLabel component="legend">Text Color</FormLabel>
        <input
          type="color"
          value={color || '#000000'}
          onChange={(e) => setProp(props => props.color = e.target.value)}
        />
      </FormControl>

      <FormControl fullWidth margin="normal" component="fieldset">
        <FormLabel component="legend">Background Color</FormLabel>
        <input
          type="color"
          value={backgroundColor || '#ffffff'}
          onChange={(e) => setProp(props => props.backgroundColor = e.target.value)}
        />
      </FormControl>

      <FormControl fullWidth margin="normal">
        <FormControlLabel
          control={
            <Checkbox
              checked={hasBorder || false}
              onChange={(e) => setProp(props => props.hasBorder = e.target.checked)}
            />
          }
          label="Enable Border"
        />
        {hasBorder && (
          <TextField
            value={border || '1px solid #e0e0e0'}
            onChange={(e) => setProp(props => props.border = e.target.value)}
            size="small"
            placeholder="e.g. 1px solid #ccc, 2px dashed red, 3px dotted blue"
            helperText="Full CSS border syntax: width style color"
            sx={{ marginTop: 1 }}
          />
        )}
      </FormControl>

      <FormControl fullWidth margin="normal">
        <FormLabel>Border Radius</FormLabel>
        <TextField
          value={borderRadius || '0px'}
          onChange={(e) => setProp(props => props.borderRadius = e.target.value)}
          size="small"
          placeholder="e.g. 0px, 5px, 0.5rem, 10%"
          helperText="Use any CSS unit: px, em, rem, %, etc."
        />
      </FormControl>

      <FormControl fullWidth margin="normal">
        <FormLabel>Padding</FormLabel>
        <TextField
          value={padding || '10px'}
          onChange={(e) => setProp(props => props.padding = e.target.value)}
          size="small"
          placeholder="e.g. 10px, 1em, 5px 10px, 1rem 2rem"
          helperText="Use any CSS unit or shorthand: px, em, rem, %, etc."
        />
      </FormControl>

      <FormControl fullWidth margin="normal">
        <FormControlLabel
          control={
            <Checkbox
              checked={hasCustomHeight || false}
              onChange={(e) => setProp(props => props.hasCustomHeight = e.target.checked)}
            />
          }
          label="Set Custom Height"
        />
        {hasCustomHeight && (
          <>
            <TextField
              value={height || '200px'}
              onChange={(e) => setProp(props => props.height = e.target.value)}
              size="small"
              placeholder="e.g. 200px, 50vh, 300px, 20rem"
              helperText="Use any CSS unit: px, vh, vw, em, rem, %, etc."
              sx={{ marginTop: 1, marginBottom: 1 }}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={enableScroll || false}
                  onChange={(e) => setProp(props => props.enableScroll = e.target.checked)}
                />
              }
              label="Enable Scroll (overflow: auto)"
              sx={{ marginTop: 1 }}
            />
          </>
        )}
      </FormControl>

      <FormControl fullWidth margin="normal">
        <FormLabel>CSS Class</FormLabel>
        <TextField
          value={cssClass || ''}
          onChange={(e) => setProp(props => props.cssClass = e.target.value)}
          size="small"
          placeholder="Enter CSS class names"
        />
      </FormControl>
    </div>
  );
};

MarkdownDisplay.craft = {
  props: {
    name: "",
    markdownContent: "# Welcome to Markdown\n\nThis is a **markdown** display component.\n\n- You can use *italic* text\n- And `inline code`\n- [Links](https://example.com) work too!\n\n```javascript\nconsole.log('Code blocks are supported!');\n```",
    fontSize: "14px",
    fontFamily: 'Arial',
    color: '#000000',
    backgroundColor: '',
    borderRadius: "0px",
    padding: "10px",
    hasBorder: true,
    border: "1px solid #e0e0e0",
    hasCustomHeight: false,
    height: "200px",
    enableScroll: false,
    cssClass: '',
    qwantumId: ''
  },
  rules: {
    canDrag: true
  },
  related: {
    settings: MarkdownDisplaySettings
  }
};